import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase/config';
import { collection, query, where, orderBy, limit, onSnapshot, addDoc, doc, getDoc, Timestamp } from 'firebase/firestore';
import { ScanLine, FilePlus, CheckCircle, Clock, UploadCloud, HardDrive, Download, Eye, HardDriveDownload } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import StlViewer from '../StlViewer';

// 통합 에이전트가 감지한 스캔 목록 (치과용)
// 에이전트가 scans 컬렉션에 문서를 생성하면 여기 실시간 표시됨
const STATUS_META = {
    detected: { label: '감지됨 (치과 PC)', color: '#64748b', bg: '#f1f5f9', Icon: HardDrive },
    uploading: { label: '전송 중', color: '#2563eb', bg: '#eff6ff', Icon: UploadCloud },
    ready: { label: '전송 완료', color: '#059669', bg: '#ecfdf5', Icon: CheckCircle },
    ordered: { label: '의뢰 완료', color: '#7c3aed', bg: '#f5f3ff', Icon: FilePlus },
    delivered: { label: '기공소 수신 완료', color: '#059669', bg: '#ecfdf5', Icon: Download },
    expired: { label: '만료됨', color: '#9ca3af', bg: '#f9fafb', Icon: Clock },
};

const SCANNER_LABELS = {
    medit: 'Medit', trios: 'TRIOS', primescan: 'Primescan', r2cad: 'R2CAD', unknown: '스캐너 미확인',
};

function ClinicRecentScans({ user }) {
    const navigate = useNavigate();
    const [scans, setScans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stlPreview, setStlPreview] = useState(null); // { url, fileName }

    const targetUserId = user?.companyId || user?.uid;
    const [detecting, setDetecting] = useState(false);
    const [showHidden, setShowHidden] = useState(false);   // 「오늘」에서 ×로 숨긴 스캔(dismissedAt) 보기 (2026-09-02)

    // [새 스캔 불러오기] — 원할 때만 에이전트가 폴더 전체를 1회 훑음 (상시 감시 아님, 2026-07-25 사장 확정)
    const requestScanDetect = async () => {
        setDetecting(true);
        try {
            // 에이전트 살아있는지 먼저 확인 (10분 내 하트비트)
            const st = await getDoc(doc(db, 'agentStatus', targetUserId));
            const devices = st.exists() ? (st.data().devices || {}) : {};
            const online = Object.values(devices).some(d => {
                const t = d?.lastSeen?.toDate ? d.lastSeen.toDate().getTime() : 0;
                return Date.now() - t < 10 * 60 * 1000;
            });
            if (!online) {
                alert('치과 PC의 덴클 프로그램이 꺼져 있어요.\n프로그램을 실행한 뒤 다시 눌러주세요.');
                setDetecting(false);
                return;
            }
            await addDoc(collection(db, 'agentCommands'), {
                labId: targetUserId,
                type: 'scan_detect',
                status: 'pending',
                createdAt: Timestamp.now(),
            });
            alert('요청 완료. 잠시 후 새 스캔이 목록에 나타납니다.');
        } catch (e) {
            console.error('감지 요청 실패:', e);
            alert('감지 요청에 실패했습니다.');
        }
        setDetecting(false);
    };

    useEffect(() => {
        if (!targetUserId) return;
        const q = query(
            collection(db, 'scans'),
            where('clinicId', '==', targetUserId),
            orderBy('detectedAt', 'desc'),
            limit(200)   // 137건 일괄 재등록으로 50건 한도에 안 보이던 환자 발생 — 확대 (2026-08-18)
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            // 정렬은 실제 스캔 시각(scannedAt = 파일 수정시각) 우선 — 감지 순서(detectedAt)가 아니라
            // 구강스캐너에서 찍은 순서대로 보이게 (대표 지시 2026-08-17). 구 문서는 detectedAt 폴백.
            const tms = (v) => (v && typeof v.toMillis === 'function') ? v.toMillis() : (v ? new Date(v).getTime() || 0 : 0);
            setScans(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => tms(b.scannedAt || b.detectedAt) - tms(a.scannedAt || a.detectedAt)));
            setLoading(false);
        }, (error) => {
            console.error('스캔 목록 조회 실패:', error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [targetUserId]);

    const formatTime = (ts) => {
        if (!ts) return '';
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        const hhmm = date.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
        return isToday ? hhmm : `${date.getMonth() + 1}/${date.getDate()} ${hhmm}`;
    };

    const formatSize = (bytes) => {
        if (!bytes) return '';
        if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
        return `${Math.round(bytes / 1024)}KB`;
    };

    return (
        <div style={styles.container}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                    <PageHeader icon={ScanLine} title="최근 스캔" />
                    <button onClick={requestScanDetect} disabled={detecting} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px',
                        border: 'none', borderRadius: 10, background: '#6366F1', color: '#fff',
                        fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    }}>
                        <ScanLine size={16} /> {detecting ? '요청 중...' : '새 스캔 불러오기'}
                    </button>
                </div>
                {scans.some(sc => sc.dismissedAt) && (
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#64748b', cursor: 'pointer', margin: '4px 0 8px' }}>
                        <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} style={{ width: 'auto' }} />
                        숨긴 스캔 보기 ({scans.filter(sc => sc.dismissedAt).length}건 — 「오늘」에서 ×로 숨긴 것)
                    </label>
                )}
                {auth.currentUser?.email === 'ivanlab@naver.com' && (
                    <button style={styles.agentButton} onClick={() => navigate('/download-agent')}>
                        <HardDriveDownload size={15} />
                        에이전트 다운로드
                    </button>
                )}
            </div>
            <div style={styles.content}>
                {loading ? (
                    <div style={styles.emptyBox}>불러오는 중...</div>
                ) : scans.length === 0 ? (
                    <div style={styles.emptyBox}>
                        <ScanLine size={36} color="#cbd5e1" />
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#475569', marginTop: 12 }}>
                            아직 감지된 스캔이 없습니다
                        </div>
                        <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 6, lineHeight: 1.6 }}>
                            덴클 에이전트를 설치하고 구강스캐너 내보내기 폴더를 등록하면<br />
                            새 스캔이 자동으로 여기에 표시됩니다.
                        </div>
                        {auth.currentUser?.email === 'ivanlab@naver.com' && (
                            <button
                                style={{ ...styles.orderButton, marginTop: 16 }}
                                onClick={() => navigate('/download-agent')}
                            >
                                <HardDriveDownload size={15} />
                                에이전트 다운로드
                            </button>
                        )}
                    </div>
                ) : (
                    scans.filter(scan => showHidden || !scan.dismissedAt).map(scan => {
                        const meta = STATUS_META[scan.status] || STATUS_META.detected;
                        const files = scan.files || [];
                        const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);
                        const canOrder = !scan.orderId && scan.status !== 'expired';
                        return (
                            <div key={scan.id} style={styles.card}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                        <span style={styles.caseName}>
                                            {scan.patientNameGuess || scan.caseLabel || scan.id}
                                        </span>
                                        <span style={{ ...styles.statusChip, color: meta.color, backgroundColor: meta.bg }}>
                                            <meta.Icon size={12} style={{ marginRight: 4 }} />
                                            {meta.label}
                                        </span>
                                    </div>
                                    <div style={styles.subInfo}>
                                        {formatTime(scan.scannedAt || scan.detectedAt)}
                                        {' · '}{SCANNER_LABELS[scan.scanner] || scan.scanner || '스캐너 미확인'}
                                        {' · '}파일 {files.length}개{totalSize ? ` (${formatSize(totalSize)})` : ''}
                                        {files.length > 0 && (
                                            <span style={{ color: '#94a3b8' }}>
                                                {' — '}{files.map(f => ({ upper: '상악', lower: '하악', bite: '교합' }[f.arch] || '추가')).join(' · ')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {scan.archive?.downloadURL && (
                                    <button
                                        style={styles.viewButton}
                                        title="3D 미리보기"
                                        onClick={() => setStlPreview({ url: scan.archive.downloadURL, fileName: `${scan.caseLabel || scan.id}.zip` })}
                                    >
                                        <Eye size={15} />
                                    </button>
                                )}
                                {canOrder ? (
                                    <>
                                        <button
                                            style={styles.orderButton}
                                            onClick={() => navigate(`/create-order?scanId=${scan.id}`)}
                                        >
                                            <FilePlus size={15} />
                                            의뢰서 작성
                                        </button>
                                        <button
                                            style={{ ...styles.orderButton, backgroundColor: '#ffffff', color: '#6366f1', border: '1px solid #c7d2fe' }}
                                            onClick={() => navigate(`/handwritten-order?scanId=${scan.id}`)}
                                        >
                                            ✍️ 필기로 작성
                                        </button>
                                    </>
                                ) : scan.orderId ? (
                                    <button
                                        style={styles.viewButton}
                                        onClick={() => navigate(`/view-order/${scan.orderId}`)}
                                    >
                                        의뢰서 보기
                                    </button>
                                ) : null}
                            </div>
                        );
                    })
                )}
            </div>
            {stlPreview && (
                <StlViewer
                    url={stlPreview.url}
                    fileName={stlPreview.fileName}
                    onClose={() => setStlPreview(null)}
                />
            )}
        </div>
    );
}

const styles = {
    container: { maxWidth: 860, margin: '0 auto', padding: '0 16px 40px' },
    content: { marginTop: 16 },
    emptyBox: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '60px 20px', textAlign: 'center',
        backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12,
        color: '#94a3b8', fontSize: 14,
    },
    card: {
        display: 'flex', alignItems: 'center', gap: 12,
        backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12,
        padding: '14px 16px', marginBottom: 10,
    },
    caseName: { fontSize: 15, fontWeight: 600, color: '#0f172a' },
    statusChip: {
        display: 'inline-flex', alignItems: 'center',
        fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
    },
    subInfo: { fontSize: 13, color: '#64748b', marginTop: 4 },
    orderButton: {
        display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        backgroundColor: '#6366f1', color: '#ffffff', border: 'none',
        borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    viewButton: {
        flexShrink: 0, backgroundColor: '#f1f5f9', color: '#475569',
        border: '1px solid #e2e8f0', borderRadius: 8,
        padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
    agentButton: {
        display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
        backgroundColor: '#ffffff', color: '#6366f1',
        border: '1px solid #c7d2fe', borderRadius: 8,
        padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
};

export default ClinicRecentScans;
